import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Paperclip, Newspaper, Loader2 } from "lucide-react";

const tokenize = (p) => {
  const token = localStorage.getItem("grindstugatan_token") || "";
  const base = `${API_BASE}/api${p.replace(/^\/api/, "")}`;
  return token ? `${base}${base.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}` : base;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function NewsDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/vklass/news/${id}`);
        if (!cancelled) setNews(data);
      } catch {
        if (!cancelled) setError("Nyheten kunde inte hittas.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <div className="px-4 pt-4 pb-24 max-w-2xl mx-auto" data-testid="news-detail-page">
      <div className="flex items-center gap-2 mb-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          data-testid="news-back-btn"
          aria-label="Tillbaka"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-[color:var(--accent-color,#3D5A80)]" />
          Nyhet från skolan
        </h1>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-[color:var(--text-tertiary)]" data-testid="news-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Laddar…
        </div>
      )}

      {!loading && error && (
        <div className="card-soft p-6 text-center text-sm text-[color:var(--text-tertiary)]" data-testid="news-error">
          {error}
        </div>
      )}

      {!loading && news && (
        <article className="card-soft p-5 space-y-4" data-testid="news-detail-card">
          <header className="space-y-2">
            <h2 className="text-base md:text-lg font-semibold leading-snug" data-testid="news-title">
              {news.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--text-tertiary)]">
              {news.published_text || fmtDate(news.published_at) ? (
                <span data-testid="news-published">{news.published_text || fmtDate(news.published_at)}</span>
              ) : null}
              {news.author && <span>· {news.author}</span>}
              {news.scope && (
                <span className="px-2 py-0.5 rounded-full bg-[#EEF2F7] text-[#3D5A80] font-medium">
                  {news.scope}
                </span>
              )}
              {(news.children || []).map((c) => (
                <span key={c} className="px-2 py-0.5 rounded-full bg-[#FDF2EE] text-[#E07A5F] font-medium">
                  {c}
                </span>
              ))}
            </div>
          </header>

          {news.body && (
            <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="news-body">
              {news.body}
            </div>
          )}

          {(news.attachments || []).length > 0 && (
            <section className="pt-2 border-t border-[color:var(--border,#eee)]">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
                Bilagor ({news.attachments.length})
              </p>
              <ul className="space-y-2">
                {news.attachments.map((a, i) => (
                  <li key={a.file_id || i}>
                    <a
                      href={a.proxy_url ? tokenize(a.proxy_url) : a.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 text-sm text-[#3D5A80] underline underline-offset-2"
                      data-testid={`news-attachment-${i}`}
                    >
                      <Paperclip className="w-4 h-4 shrink-0" />
                      {a.filename || a.title || "Bilaga"}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </article>
      )}
    </div>
  );
}
